import React, { useState, useEffect, useCallback } from 'react';
import {
  Inbox,
  Clock,
  Phone,
  Mail,
  MessageCircle,
  AlertCircle,
  Save,
  Trash2,
  User,
} from 'lucide-react';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Badge from '../components/Badge';
import { useToast } from '../components/Toast';
import { queries } from '../../lib/supabase/queries';
import { isSupabaseConfigured } from '../../lib/supabase/client';

export default function AdminEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Admin notes & status editing
  const [status, setStatus] = useState('new');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const loadEnquiries = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      setError('Supabase connection is not configured in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await queries.enquiries.getAll('all');
      setEnquiries(data);
    } catch (err) {
      console.error('Error loading enquiries:', err);
      setError(err.message || 'Failed to fetch enquiries from Supabase.');
      toast.error('Failed to load enquiries: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadEnquiries();
  }, [loadEnquiries]);

  const handleOpenDetail = (enq) => {
    setSelectedEnquiry(enq);
    setStatus(enq.status || 'new');
    setAdminNotes(enq.admin_notes || '');
    setModalOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedEnquiry) return;

    setSaving(true);
    try {
      await queries.enquiries.updateStatus(selectedEnquiry.id, status, adminNotes);
      toast.success('Enquiry record updated in Supabase');
      setModalOpen(false);
      loadEnquiries();
    } catch (err) {
      console.error('Error updating enquiry:', err);
      toast.error('Failed to update: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEnquiry) return;
    try {
      await queries.enquiries.delete(selectedEnquiry.id);
      toast.success('Enquiry lead deleted from Supabase');
      setConfirmOpen(false);
      setModalOpen(false);
      setSelectedEnquiry(null);
      loadEnquiries();
    } catch (err) {
      console.error('Error deleting enquiry:', err);
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'new':
        return <Badge variant="warning">NEW LEAD</Badge>;
      case 'contacted':
        return <Badge variant="info">CONTACTED</Badge>;
      case 'in_progress':
        return <Badge variant="info">IN PROGRESS</Badge>;
      case 'completed':
        return <Badge variant="success">COMPLETED</Badge>;
      case 'archived':
        return <Badge variant="default">ARCHIVED</Badge>;
      default:
        return <Badge variant="default">{s.toUpperCase()}</Badge>;
    }
  };

  const columns = [
    {
      header: 'Client Details',
      accessor: (row) => (
        <div>
          <div className="font-bold text-sm text-slate-100">{row.name}</div>
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
            <span>{row.email}</span>
            {row.phone && <span className="font-mono text-slate-500">· {row.phone}</span>}
          </div>
        </div>
      ),
    },
    {
      header: 'Subject & Inquiry Message',
      accessor: (row) => (
        <div className="max-w-md">
          {row.subject && (
            <div className="text-xs font-semibold text-[#9BB7A3] mb-0.5 truncate">
              {row.subject}
            </div>
          )}
          <p className="text-xs text-slate-300 line-clamp-2">
            {row.message}
          </p>
        </div>
      ),
    },
    {
      header: 'Status',
      accessor: (row) => getStatusBadge(row.status),
    },
    {
      header: 'Received Date',
      accessor: (row) => (
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Clock size={12} />
          <span>{new Date(row.created_at).toLocaleDateString()}</span>
        </div>
      ),
    },
    {
      header: 'Quick Action',
      className: 'text-right',
      accessor: (row) => {
        const cleanPhone = (row.phone || '').replace(/[^0-9]/g, '');
        const whatsappUrl = cleanPhone
          ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(
              `Hi ${row.name}, thank you for reaching out to VJ Dsign Studio!`
            )}`
          : null;

        return (
          <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            {row.phone && (
              <a
                href={`tel:${row.phone}`}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition"
                title="Call Client"
              >
                <Phone size={14} />
              </a>
            )}
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-xl bg-emerald-950/80 hover:bg-emerald-600 text-emerald-400 hover:text-white transition"
                title="Chat on WhatsApp"
              >
                <MessageCircle size={14} />
              </a>
            )}
            {row.email && (
              <a
                href={`mailto:${row.email}?subject=VJ Dsign Studio - Inquiry Response`}
                className="p-2 rounded-xl bg-slate-800 hover:bg-[#9BB7A3] text-slate-300 hover:text-slate-950 transition"
                title="Email Client"
              >
                <Mail size={14} />
              </a>
            )}
            <button
              onClick={() => handleOpenDetail(row)}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-200 transition"
            >
              Details
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900/60 p-6 rounded-3xl border border-slate-800">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <Inbox size={24} className="text-[#9BB7A3]" />
            Client Inquiries & Lead Management
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Track customer contact form submissions, record internal notes, and instantly connect with leads.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border border-rose-800/40 text-rose-300 text-xs flex items-center gap-2.5">
          <AlertCircle size={16} className="shrink-0 text-rose-400" />
          <span>{error}</span>
        </div>
      )}

      {/* Main DataTable */}
      <DataTable
        loading={loading}
        columns={columns}
        data={enquiries}
        searchKey="name"
        searchPlaceholder="Search leads by client name..."
        filters={[
          {
            key: 'status',
            label: 'Filter Status',
            options: [
              { label: 'New Leads', value: 'new' },
              { label: 'Contacted', value: 'contacted' },
              { label: 'In Progress', value: 'in_progress' },
              { label: 'Completed', value: 'completed' },
              { label: 'Archived', value: 'archived' },
            ],
          },
        ]}
        emptyIcon={<Inbox size={40} className="text-slate-600" />}
        emptyTitle="No Enquiries Found"
        emptyDescription="Inquiries submitted through the website contact form will appear here in real time."
      />

      {/* Detailed Enquiry Modal */}
      {selectedEnquiry && (
        <Modal
          isOpen={modalOpen}
          onClose={() => !saving && setModalOpen(false)}
          title="Lead Details & Action Response"
          maxWidth="max-w-2xl"
        >
          <form onSubmit={handleUpdate} className="space-y-6">
            {/* Lead Info Banner */}
            <div className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <User size={18} className="text-[#9BB7A3]" />
                    {selectedEnquiry.name}
                  </h3>
                  <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                    <div>Email: <a href={`mailto:${selectedEnquiry.email}`} className="text-slate-200 hover:underline">{selectedEnquiry.email}</a></div>
                    {selectedEnquiry.phone && (
                      <div>Phone: <a href={`tel:${selectedEnquiry.phone}`} className="text-slate-200 hover:underline">{selectedEnquiry.phone}</a></div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {selectedEnquiry.phone && (
                    <a
                      href={`tel:${selectedEnquiry.phone}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
                    >
                      <Phone size={13} />
                      <span>Call</span>
                    </a>
                  )}
                  {selectedEnquiry.phone && (
                    <a
                      href={`https://wa.me/${selectedEnquiry.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
                        `Hi ${selectedEnquiry.name}, thank you for reaching out to VJ Dsign Studio!`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-950/80 hover:bg-emerald-600 text-emerald-300 hover:text-white text-xs font-semibold transition"
                    >
                      <MessageCircle size={13} />
                      <span>WhatsApp</span>
                    </a>
                  )}
                  {selectedEnquiry.email && (
                    <a
                      href={`mailto:${selectedEnquiry.email}?subject=VJ Dsign Studio - Inquiry Response`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-[#9BB7A3] text-slate-200 hover:text-slate-950 text-xs font-semibold transition"
                    >
                      <Mail size={13} />
                      <span>Email</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Inquiry Message Text */}
              <div className="p-4 bg-slate-900/80 rounded-xl border border-slate-800/80">
                <div className="text-[11px] font-bold text-[#9BB7A3] uppercase tracking-wider mb-1">
                  Subject: {selectedEnquiry.subject || 'General Inquiry'}
                </div>
                <p className="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">
                  {selectedEnquiry.message}
                </p>
                <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                  <Clock size={11} />
                  <span>Submitted on {new Date(selectedEnquiry.created_at).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Status Selector */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Lead Progress Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 focus:outline-none focus:border-[#9BB7A3]"
              >
                <option value="new">New Lead (Awaiting Response)</option>
                <option value="contacted">Contacted (In Discussion)</option>
                <option value="in_progress">In Progress (Design / Quote Sent)</option>
                <option value="completed">Completed (Project Won / Closed)</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Admin Internal Notes */}
            <div>
              <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                Admin Internal Notes
              </label>
              <textarea
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Log internal details: budget discussed, design consultation meeting date, quotation sent..."
                className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-[#9BB7A3]"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                className="px-4 py-2 rounded-xl bg-rose-950/60 hover:bg-rose-600 text-rose-300 hover:text-white text-xs font-semibold border border-rose-800/60 transition flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                <span>Delete Lead</span>
              </button>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-white transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-[#9BB7A3] hover:bg-[#8AA692] text-slate-950 font-bold text-xs uppercase tracking-wider transition shadow-lg shadow-[#9BB7A3]/20 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Save size={15} />
                  <span>{saving ? 'Saving...' : 'Update Lead'}</span>
                </button>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Lead Record"
        message={`Are you sure you want to delete the enquiry record from "${selectedEnquiry?.name}"?`}
        confirmText="Delete Record"
        type="danger"
      />
    </div>
  );
}
